/**
 * Cierre de mes del **kardex valorizado**.
 *
 * Es el diálogo del botón *Cerrar Mes* de `views/inventory/kardex_valorizado.php`: deja
 * guardado el **saldo final** del período (cantidad, costo unitario y costo total) en
 * `cierre_mes_productos`, que es lo que abre el kardex del mes siguiente.
 *
 * Reglas heredadas:
 * - El motivo es **opcional**.
 * - No se puede cerrar con una **cantidad negativa** (el original lo avisaba en el
 *   navegador; aquí también lo comprueba la API).
 *
 * Diferencias anotadas: el cierre **no se puede repetir** para el mismo producto y mes
 * (la API responde 409), cosa que el original no comprobaba.
 */

import { Lock } from 'lucide-react';
import { useState } from 'react';

import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { useCerrarMesValorizado } from '../kardex-valorizado.api';
import type { ReporteValorizado } from '../kardex-valorizado.types';

/** Cantidad con tres decimales, para el resumen del cierre. */
function cantidadLegible(valor: number): string {
  return valor.toLocaleString('es-PE', { minimumFractionDigits: 3, maximumFractionDigits: 3 });
}

export function CierreMesDialog({
  open,
  onClose,
  datos,
  mes,
  anio,
  onAviso,
}: {
  open: boolean;
  onClose: () => void;
  datos: ReporteValorizado;
  mes: number;
  anio: number;
  onAviso: (mensaje: string) => void;
}) {
  const cerrar = useCerrarMesValorizado();

  const [motivo, setMotivo] = useState('');
  const [error, setError] = useState<string | null>(null);

  const negativo = datos.saldoFinal.cantidad < 0;

  const enviar = () => {
    setError(null);

    cerrar.mutate(
      {
        product_id: datos.productoId,
        month: mes,
        year: anio,
        quantity: datos.saldoFinal.cantidad,
        price: datos.saldoFinal.precio,
        total: datos.saldoFinal.total,
        reason: motivo.trim(),
      },
      {
        onSuccess: () => {
          onAviso(
            `Mes ${String(mes).padStart(2, '0')}/${anio} cerrado con ${cantidadLegible(
              datos.saldoFinal.cantidad,
            )} de ${datos.producto}.`,
          );
          setMotivo('');
          onClose();
        },
        onError: (fallo: unknown) =>
          setError(fallo instanceof Error ? fallo.message : 'No se pudo cerrar el mes.'),
      },
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={`Cerrar el mes ${String(mes).padStart(2, '0')}/${anio}`}
      description="Se guarda el saldo final del período para que sea el saldo inicial del mes siguiente."
      size="md"
      footer={
        <div className="flex flex-wrap justify-end gap-2">
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            leftIcon={<Lock className="size-4" aria-hidden="true" />}
            disabled={negativo}
            loading={cerrar.isPending}
            onClick={enviar}
          >
            Confirmar cierre
          </Button>
        </div>
      }
    >
      <div className="space-y-3">
        {error ? (
          <Alert variant="error" title="No se pudo cerrar el mes">
            {error}
          </Alert>
        ) : null}

        {negativo ? (
          <Alert variant="error" title="El saldo final es negativo">
            No se puede cerrar el mes con una cantidad negativa (
            {cantidadLegible(datos.saldoFinal.cantidad)}). Revisa los movimientos del período antes
            de cerrarlo.
          </Alert>
        ) : (
          <div className="grid grid-cols-1 gap-3 rounded-card border border-ink-100 p-3 sm:grid-cols-3">
            <div>
              <p className="text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
                Cantidad
              </p>
              <p className="text-base font-bold text-ink-900 tabular-nums">
                {cantidadLegible(datos.saldoFinal.cantidad)}
              </p>
            </div>
            <div>
              <p className="text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
                Costo unitario
              </p>
              <p className="text-base font-bold text-ink-900 tabular-nums">
                {cantidadLegible(datos.saldoFinal.precio)}
              </p>
            </div>
            <div>
              <p className="text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
                Costo total
              </p>
              <p className="text-base font-bold text-ink-900 tabular-nums">
                {cantidadLegible(datos.saldoFinal.total)}
              </p>
            </div>
          </div>
        )}

        <label className="flex flex-col gap-1 text-xs font-medium text-ink-500">
          Motivo del cierre (opcional)
          <textarea
            className="min-h-20 w-full rounded-control border border-ink-200 bg-white px-3 py-2 text-sm text-ink-900 transition-colors focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none"
            value={motivo}
            maxLength={1000}
            aria-label="Motivo del cierre"
            placeholder="Por ejemplo: cierre mensual con inventario físico conciliado"
            onChange={(evento) => setMotivo(evento.target.value)}
          />
        </label>
      </div>
    </Dialog>
  );
}
